import { useWindowResize } from '@toggle/helpers';
import { getInstanceByDom, init } from 'echarts';
import * as Types from 'echarts/types/dist/echarts';
import React, { useEffect, useRef } from 'react';

import {
  ApacheChartBase,
  createApacheConfig,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
  Y_AXIS_LABEL_PERCENTAGE,
} from '../apache-config';
import { tooltipFormatter } from '../utils';

export type SankeyLink = {
  source: string;
  target: string;
  value: number;
};

export interface BasicSankeyChartProps extends ApacheChartBase {
  chartType: 'apache-sankey';
  categories: string[];
  links: SankeyLink[];
  isVertical?: boolean;
}

export const BasicSankeyChart = ({
  theme = 'dark_default',
  chartType,
  categories,
  links,
  isVertical = false,
  onInit,
  isModal,
}: BasicSankeyChartProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { width, height } = useWindowResize({ targetRef: ref });

  useEffect(() => {
    if (ref.current) {
      const chartDom = getInstanceByDom(ref.current);
      chartDom?.resize({ width, height });
    }
  }, [width, height]);

  useEffect(() => {
    const chartDom = init(ref.current, theme, {
      width: width || DEFAULT_CHART_WIDTH,
      height: height || DEFAULT_CHART_HEIGHT,
    });

    const chartWidth = width || DEFAULT_CHART_WIDTH;
    const yAxisLabelWidth = chartWidth * Y_AXIS_LABEL_PERCENTAGE;

    const option = createApacheConfig({
      theme,
      isModal,
      yAxisLabelWidth,
      options: {
        animationDuration: 2_000,
        tooltip: {
          formatter: params => {
            return tooltipFormatter({
              isShowTitle: false,
              isShowMarker: false,
              params: params as Types.DefaultLabelFormatterCallbackParams,
            });
          },
        },
        series: {
          type: 'sankey',
          orient: isVertical ? 'vertical' : 'horizontal',
          data: categories.map(i => ({ name: i })),
          links: links.map(i => ({
            ...i,
            lineStyle: { color: 'source' },
          })),
        },
      },
    });

    chartDom.setOption(option);

    if (ref.current) {
      onInit?.(ref.current);
    }

    return () => {
      chartDom.dispose();
    };
  }, [isModal, chartType, theme, categories, links, isVertical, width, height]);

  return <div ref={ref} />;
};
